import { API_URL } from '../constants';

/**
 * Send a message to the AI chat backend
 * @param {string} message - The user's message
 * @param {object} context - Current ABLE state (tasks, filters, etc)
 * @param {array} conversationHistory - Previous messages in conversation
 * @param {boolean} fullContext - Whether to send full task details
 * @param {object} pendingEpisode - Pending episode suggestion (if any)
 * @param {string} accessToken - Kinde access token for authentication
 * @param {boolean} isOnboarding - Whether this is an onboarding conversation
 * @returns {Promise<object>} Response with message and suggested tasks
 */
export async function sendChatMessage(message, context, conversationHistory = [], fullContext = false, pendingEpisode = null, accessToken = null, isOnboarding = false) {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add Bearer token if provided
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const response = await fetch(`${API_URL}/api/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message,
        context,
        conversationHistory,
        fullContext,
        pendingEpisode,
        isOnboarding
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }
    
    return response.json();
  } catch (error) {
    console.error('Chat API error:', error);
    throw error;
  }
}

/**
 * Build inventory context for kitchen tools
 * @param {array} stockedItems - Items in pantry
 * @param {array} shoppingList - Items on shopping list
 * @returns {object} Inventory context object
 */
export function buildInventoryContext(stockedItems = [], shoppingList = []) {
  return {
    stockedCount: stockedItems.length,
    shoppingCount: shoppingList.length,
    stockedItems: stockedItems.map(i => ({
      id: i.id,
      name: i.name,
      category: i.category,
      quantity: i.quantity,
      status: i.status
    })),
    shoppingList: shoppingList.map(i => ({
      id: i.id,
      name: i.name,
      category: i.category,
      quantity: i.quantity
    })),
    // Summary for system prompt
    pantryItems: stockedItems.map(i => ({
      name: i.name,
      category: i.category
    }))
  };
}

/**
 * Build light context from current ABLE state
 * @param {array} tasks - All tasks
 * @param {object} capacity - Current filter state
 * @param {string} viewMode - Current view mode
 * @param {object} currentTask - Current task in focus mode (if any)
 * @param {object} inventoryContext - Optional inventory context for kitchen
 * @param {string} statusFilter - Current status filter ('active', 'done', 'all', etc.)
 * @param {object} mealPlanContext - Optional meal plan context
 * @returns {object} Light context object
 */
export function buildLightContext(tasks, capacity, viewMode, currentTask = null, inventoryContext = null, statusFilter = 'active', mealPlanContext = null) {
  // Get current time info for circadian awareness
  const now = new Date();
  const hour = now.getHours();
  const timeOfDay = hour < 6 ? 'night' : 
                   hour < 12 ? 'morning' : 
                   hour < 17 ? 'afternoon' : 
                   hour < 21 ? 'evening' : 'night';
  const currentTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
  const currentDate = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // Include task list with IDs for tool operations (complete/update/delete)
  const taskList = tasks.map(t => ({
    id: t.id,
    title: t.title,
    priority: t.priority,
    project: t.project || null,
    area: t.area || null,
    done: t.done,
    notes: t.notes || ''
  }));

  // Extract unique projects from tasks
  const existingProjects = [...new Set(tasks.map(t => t.project).filter(Boolean))];

  // Valid areas for reference
  const validAreas = ['Home', 'Work', 'Games', 'Apps', 'Family', 'Finance', 'Health', 'Social'];

  // Count done tasks for context
  const doneTasks = tasks.filter(t => t.done).length;
  const activeTasks = tasks.filter(t => !t.done).length;

  const context = {
    currentTime: currentTime,
    currentDay: currentDay,
    currentDate: currentDate,
    timeOfDay: timeOfDay,
    totalTasks: tasks.length,
    activeTasks: activeTasks,
    doneTasks: doneTasks,
    statusFilter: statusFilter,  // Current status filter
    currentFilters: capacity,
    viewMode: viewMode,
    currentTask: currentTask?.title || null,
    tasks: taskList,  // Include tasks for function calling
    existingProjects,  // Help Mabel know what projects exist
    validAreas  // Help Mabel know valid area options
  };

  // Add inventory context if provided
  if (inventoryContext) {
    context.inventory = inventoryContext;
  }

  // Add meal plan context if provided
  if (mealPlanContext) {
    context.mealPlan = mealPlanContext;
  }

  return context;
}

/**
 * Build full context with task details
 * @param {array} tasks - All tasks
 * @param {array} filteredTasks - Currently visible tasks
 * @param {object} capacity - Current filter state
 * @param {string} viewMode - Current view mode
 * @param {object} currentTask - Current task in focus mode (if any)
 * @param {function} isTaskBlocked - Function to check if task is blocked
 * @returns {object} Full context object
 */
export function buildFullContext(tasks, filteredTasks, capacity, viewMode, currentTask, isTaskBlocked) {
  const lightContext = buildLightContext(tasks, capacity, viewMode, currentTask);
  
  return {
    ...lightContext,
    tasks: filteredTasks.map(t => ({
      title: t.title,
      priority: t.priority,
      area: t.area,
      project: t.project,
      done: t.done,
      blocked: isTaskBlocked(t, tasks),
      notes: t.notes
    }))
  };
}
